import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  TextField,
  List,
  ListItem,
  Typography,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from 'react-redux';
import { setPosition } from '../redux/mapSlice';
import { SearchResult } from '../interface/SearchResult';
import { useTranslation } from 'react-i18next';
import { addMessage } from '../redux/infoMessageSlice';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

const Search: React.FC = () => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState<string>('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showTextField, setShowTextField] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');
  const auth: string = useAuthHeader() || '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node)
      ) {
        setSearchInput('');
        setResults([]);
        setShowTextField(false);
        setHasSearched(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const performSearch = async (query: string) => {
    const { REACT_APP_API_URL } = process.env;
    const localOsmUrl = `${REACT_APP_API_URL}/osm-search/?search=${encodeURIComponent(query)}`;
    const localPoiUrl = `${REACT_APP_API_URL}/local-search/?search=${encodeURIComponent(query)}`;
    const osmUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json`;

    setIsLoading(true);
    setHasSearched(true);
    try {
      const [localOsmResponse, localPoiResponse, osmResponse] =
        await Promise.all([
          fetch(localOsmUrl, {
            headers: { 'User-Agent': 'SwissEmergencyMap/1.0' },
          }),
          fetch(localPoiUrl, {
            headers: {
              'User-Agent': 'SwissEmergencyMap/1.0',
              Authorization: auth,
            },
          }),
          fetch(osmUrl, {
            headers: { 'User-Agent': 'SwissEmergencyMap/1.0' },
          }),
        ]);

      let localOsmResults: SearchResult[] = [];
      let localPoiResults: SearchResult[] = [];
      let osmResults: SearchResult[] = [];

      if (localOsmResponse.ok) {
        const localOsmData = await localOsmResponse.json();
        localOsmResults = localOsmData.map(
          (result: {
            latitude: string | number;
            longitude: string | number;
            name?: string;
            category?: { name: string };
          }) => ({
            name: result.name || '',
            category: result.category || { name: 'Unknown' },
            latitude:
              typeof result.latitude === 'string'
                ? parseFloat(result.latitude)
                : result.latitude,
            longitude:
              typeof result.longitude === 'string'
                ? parseFloat(result.longitude)
                : result.longitude,
          })
        );
      }

      if (localPoiResponse.ok) {
        const localPoiData = await localPoiResponse.json();
        localPoiResults = localPoiData.map(
          (result: {
            latitude: string | number;
            longitude: string | number;
            title: string;
            category: { name: string };
          }) => ({
            latitude:
              typeof result.latitude === 'string'
                ? parseFloat(result.latitude)
                : result.latitude,
            longitude:
              typeof result.longitude === 'string'
                ? parseFloat(result.longitude)
                : result.longitude,
            name: result.title,
            category: result.category,
          })
        );
      }

      if (osmResponse.ok) {
        const osmData = await osmResponse.json();
        osmResults = osmData.map(
          (result: {
            lat: string | number;
            lon: string | number;
            display_name: string;
          }) => ({
            latitude:
              typeof result.lat === 'string'
                ? parseFloat(result.lat)
                : result.lat,
            longitude:
              typeof result.lon === 'string'
                ? parseFloat(result.lon)
                : result.lon,
            name: result.display_name,
            category: { name: 'OpenStreetMap' },
          })
        );
      }

      const localOsmWithName = localOsmResults.filter(
        (r) => r.name && r.name.trim().length > 0
      );
      const localOsmWithoutName = localOsmResults.filter(
        (r) => !r.name || r.name.trim().length === 0
      );

      const combinedResults = [
        ...localPoiResults,
        ...localOsmWithName,
        ...osmResults,
        ...localOsmWithoutName,
      ];

      setResults(combinedResults.slice(0, 40)); // Limit to 40 results
    } catch (error) {
      console.error('Search error:', error);
      dispatch(
        addMessage({
          message: 'error.failedToFetchSearchResults',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchButtonClick = () => {
    if (showTextField) {
      if (searchInput.trim()) {
        performSearch(searchInput);
      } else {
        dispatch(addMessage({ message: 'error.searchQuery' }));
      }
    } else {
      setShowTextField(true);
      setHasSearched(false);
    }
  };

  const handleLocationSelect = (latitude: number, longitude: number) => {
    dispatch(
      setPosition({
        latitude: latitude,
        longitude: longitude,
      })
    );
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchInput('');
        setResults([]);
        setShowTextField(false);
        setHasSearched(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Box
      ref={searchBoxRef}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        backgroundColor: showTextField ? '#ffffff' : 'transparent',
        position: 'absolute',
        top: 50,
        left: 22,
        color: 'black',
        borderRadius: 2,
        boxShadow: showTextField ? '0px 4px 6px rgba(0, 0, 0, 0.1)' : 'none',
        maxWidth: isMobile ? '70%' : '400px',
        height: 'auto',
        padding: 1,
        zIndex: 1000,
        p: 0,
      }}
    >
      <Box display="flex" alignItems="center" width="100%">
        {showTextField && (
          <TextField
            fullWidth
            placeholder={
              t('search.searchForLocation') || 'Search for a location'
            }
            name="location"
            id="searchInput"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (searchInput.trim()) {
                  performSearch(searchInput);
                } else {
                  dispatch(addMessage({ message: 'error.searchQuery' }));
                }
              }
            }}
            sx={{
              '.MuiOutlinedInput-root': {
                padding: 0,
                '& fieldset': {
                  border: 'none',
                },
              },
              '& input': {
                padding: '8px',
              },
              borderRadius: '30px',
              marginRight: '8px',
            }}
          />
        )}

        <IconButton
          onClick={handleSearchButtonClick}
          sx={{
            backgroundColor: '#ffffff',
            color: '#70757a',
            boxShadow: showTextField
              ? 'none'
              : '0px 4px 8px rgba(0, 0, 0, 0.6)',
            borderRadius: showTextField ? '0 20% 20% 0' : '50%',
            height: showTextField ? '40px' : undefined,
            width: showTextField ? '40px' : undefined,
            '&:hover': {
              backgroundColor: '#f7f7f8',
            },
          }}
        >
          <SearchIcon {...(!showTextField && { fontSize: 'medium' })} />
        </IconButton>
      </Box>

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            padding: '16px',
            marginTop: 2,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {!isLoading && results.length > 0 && (
        <List
          id={'results-list'}
          sx={{
            width: '100%',
            maxHeight: '200px',
            overflowY: 'auto',
            marginTop: 2,
            padding: 0,
          }}
        >
          {results.map((result, index) => (
            <ListItem
              key={index}
              sx={{
                padding: '8px 16px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
              onClick={() =>
                handleLocationSelect(result.latitude, result.longitude)
              }
            >
              <Typography variant="body1" sx={{ color: 'black', mr: 1 }}>
                {result.name || t('search.noName')} ({result.category.name})
                <br></br>
                <Typography sx={{ fontSize: 11 }}>
                  ({result.latitude}, {result.longitude})
                </Typography>
              </Typography>
            </ListItem>
          ))}
        </List>
      )}
      {!isLoading && results.length === 0 && showTextField && hasSearched && (
        <Typography
          variant="body2"
          sx={{ color: 'black', padding: '8px', mt: 2 }}
        >
          {t('search.noResults')}
        </Typography>
      )}
    </Box>
  );
};

export default Search;
